import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE_URL } from '../../config';
import { Session } from '../utils/session';

function toInputDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function toDisplayDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

export default function TaskCardModal({
  card,
  statusName,
  users,
  priorities,
  onClose,
  onSaved,
  onDeleted,
}) {
  const token = Session.getToken();
  const currentUserId = Session.getUserId();

  const [claimant, setClaimant] = useState('');
  const [description, setDescription] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [priorityText, setPriorityText] = useState('');
  const [cpid, setCpid] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showUserList, setShowUserList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!card) return;
    const titleParts = (card.cardTitle || '').split(' - ');
    setClaimant(titleParts[0] || card.cardTitle || '');
    setDescription(card.description || '');
    setAssignedUserId(card.assignedUserID ? String(card.assignedUserID) : '');
    setPriorityText(card.priorityName || '');
    setCpid(card.cpid ? String(card.cpid) : '');
    setDueDate(toInputDate(card.dueDate));
    setError(null);
  }, [card]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''} ${u.userName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [users, userSearch]);

  const selectedUserLabel = useMemo(() => {
    if (!assignedUserId) return 'Select..';
    const u = users.find((x) => String(x.userId) === String(assignedUserId));
    if (!u) return 'Select..';
    return u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.userName;
  }, [assignedUserId, users]);

  const handleSave = async () => {
    if (!card) return;
    setSaving(true);
    setError(null);
    try {
      const matchedPriority = priorities.find(
        (p) => p.priorityName?.toLowerCase() === priorityText.trim().toLowerCase()
      );
      const resolvedCpid = matchedPriority?.cpid ?? (cpid ? Number(cpid) : null);

      const res = await fetch(`${API_BASE_URL}/api/Cards/card-update`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cardID: card.cardID,
          cardTitle: claimant.trim(),
          description: description,
          assignedUserID: assignedUserId ? Number(assignedUserId) : 0,
          cpid: resolvedCpid,
          dueDate: dueDate ? new Date(dueDate).toISOString() : null,
          createdUserID: Number(currentUserId) || 0,
        }),
      });
      if (!res.ok) {
        let message = `Failed to save changes (HTTP ${res.status})`;
        try {
          const errBody = await res.json();
          message = errBody.detail || errBody.message || message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!card || !window.confirm('Delete this card?')) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Cards/${card.cardID}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete card');
      onDeleted && onDeleted();
      onClose();
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (!card) return null;

  return createPortal(
    <div className="task-modal-overlay" onClick={onClose}>
      <div className="task-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="task-modal-close-x" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <div className="task-modal-header">
          <div className="task-modal-header-icon">
            <i className="fas fa-list-ul" />
          </div>
          <div className="task-modal-header-text">
            <h2 className="task-modal-title">{card.cardTitle}</h2>
            <p className="task-modal-subtitle">in list: {statusName || card.statusName || 'OPEN'}</p>
          </div>
        </div>

        <div className="task-modal-body">
          <div className="task-modal-grid">
            <div className="task-modal-field">
              <label>CLAIMANT / CASE ID</label>
              <textarea
                rows={3}
                value={claimant}
                onChange={(e) => setClaimant(e.target.value)}
              />
            </div>
            <div className="task-modal-field">
              <label>DESCRIPTION</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder=""
              />
            </div>
            <div className="task-modal-field">
              <label>ASSIGNED TO</label>
              <div className="task-modal-user-select">
                <button
                  type="button"
                  className="task-modal-user-trigger"
                  onClick={() => setShowUserList((s) => !s)}
                >
                  <i className="fas fa-user" />
                  <span>{selectedUserLabel}</span>
                  <i className="fas fa-chevron-down task-modal-chevron" />
                </button>
                {showUserList && (
                  <div className="task-modal-user-dropdown">
                    <div className="task-modal-user-search">
                      <i className="fas fa-search" />
                      <input
                        type="text"
                        placeholder="Select.."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </div>
                    <div className="task-modal-user-list">
                      <label className="task-modal-user-option">
                        <input
                          type="radio"
                          name="assignedUser"
                          checked={!assignedUserId}
                          onChange={() => setAssignedUserId('')}
                        />
                        <span>Unassigned</span>
                      </label>
                      {filteredUsers.map((u) => (
                        <label key={u.userId} className="task-modal-user-option">
                          <input
                            type="radio"
                            name="assignedUser"
                            checked={String(assignedUserId) === String(u.userId)}
                            onChange={() => {
                              setAssignedUserId(String(u.userId));
                              setShowUserList(false);
                            }}
                          />
                          <span>
                            {u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.userName}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="task-modal-field">
              <label>PRIORITY</label>
              {priorities && priorities.length > 0 ? (
                <select
                  className="task-modal-priority-select"
                  value={priorityText}
                  onChange={(e) => setPriorityText(e.target.value)}
                >
                  <option value="">No Priority</option>
                  {priorities.map((p) => (
                    <option key={p.cpid} value={p.priorityName || ''}>
                      {p.priorityName}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="Enter priority"
                  value={priorityText}
                  onChange={(e) => setPriorityText(e.target.value)}
                />
              )}
            </div>
            <div className="task-modal-field">
              <label>DATE</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
              {dueDate && (
                <span className="task-modal-date-hint">{toDisplayDate(dueDate)}</span>
              )}
            </div>
          </div>

          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        </div>

        <div className="task-modal-footer">
          <button
            type="button"
            className="task-modal-btn task-modal-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            <i className="fas fa-save" /> Save Changes
          </button>
          <button
            type="button"
            className="task-modal-btn task-modal-btn-delete"
            onClick={handleDelete}
            disabled={deleting}
          >
            <i className="fas fa-trash-alt" /> Delete
          </button>
          <button type="button" className="task-modal-btn task-modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
